import DOMPurify from 'isomorphic-dompurify';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';

/**
 * Clean form data by removing empty / null values from optional fields.
 *
 * This function recursively processes form data to prepare it for submission.
 * It trims strings, filters out empty arrays and objects, and handles special
 * cases for specific config types.
 *
 * The cleaning process ensures that only meaningful data is sent to the server,
 * removing empty strings, null values, and empty objects/arrays.
 *
 * @param formDataValue - The raw form data to clean
 * @param schemaObj - The JSON schema for the current field
 * @param fieldPath - Current path in the data tree (for special case detection)
 * @returns Cleaned data or undefined if the field should be removed
 */
export const cleanFormData = <T = unknown>(
  formDataValue: T | undefined | null,
  schemaObj: JSONSchema7 | JSONSchema7Definition,
  fieldPath: string[] = [],
): T | undefined => {
  if (formDataValue === null || formDataValue === undefined) return undefined;

  // Handle string values
  if (typeof formDataValue === 'string') {
    const trimmed = DOMPurify.sanitize(formDataValue.trim());
    return trimmed === '' ? undefined : (trimmed as T);
  }

  // Handle arrays
  if (Array.isArray(formDataValue)) {
    const itemSchema =
      typeof schemaObj !== 'boolean' && schemaObj?.items
        ? (schemaObj.items as JSONSchema7Definition)
        : undefined;
    const cleanedArray = formDataValue
      .map((item, index) =>
        cleanFormData(item, itemSchema || {}, [...fieldPath, String(index)]),
      )
      .filter(item => item !== undefined);

    return (cleanedArray.length > 0 ? cleanedArray : undefined) as T;
  }

  // Handle objects
  if (typeof formDataValue === 'object' && formDataValue !== null) {
    const cleaned: Record<string, unknown> = {};
    let hasValidFields = false;

    Object.entries(formDataValue as Record<string, unknown>).forEach(
      ([key, value]) => {
        const propSchema =
          typeof schemaObj !== 'boolean' && schemaObj?.properties?.[key];
        const cleanedValue = cleanFormData(value, propSchema || {}, [
          ...fieldPath,
          key,
        ]);

        // Only include the field if it has a valid value
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
          hasValidFields = true;
        }
      },
    );

    // If the object has no valid fields, check if it's optional
    if (!hasValidFields) {
      // Return undefined to signal this object should be removed
      return undefined;
    }

    // Check if this is an object where all fields are null / empty
    const allFieldsEmpty = Object.values(cleaned).every(
      val => val === null || val === undefined || val === '',
    );

    if (allFieldsEmpty) {
      return undefined;
    }

    return cleaned as T;
  }

  return formDataValue as T;
};
